/**
 * Attachment Uploader Component
 * File upload with 72-hour retention warning
 */

import React, { useRef } from 'react';
import {
  makeStyles,
  Button,
  Text,
  Card,
  CardHeader,
  ProgressBar,
} from '@fluentui/react-components';
import { AttachRegular, DismissRegular, DocumentRegular } from '@fluentui/react-icons';
import { formatFileSize } from '../../../../utils/memoUtils';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  uploadButton: {
    alignSelf: 'flex-start',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '8px',
  },
  fileCard: {
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    border: '1px solid #e1dfdd',
    borderRadius: '4px',
  },
  fileIcon: {
    fontSize: '24px',
    color: '#008272',
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    fontSize: '12px',
    color: '#605e5c',
  },
  removeButton: {
    minWidth: 'auto',
  },
  warning: {
    padding: '8px 12px',
    backgroundColor: '#fff4ce',
    border: '1px solid #f7e06e',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#323130',
  },
});

export interface AttachmentFile {
  file: File;
  id: string;
  progress?: number;
}

interface AttachmentUploaderProps {
  attachments: AttachmentFile[];
  onAttachmentsChange: (attachments: AttachmentFile[]) => void;
  maxFiles?: number;
  maxSizePerFile?: number;
  allowedTypes?: string[];
}

export const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  attachments,
  onAttachmentsChange,
  maxFiles = 10,
  maxSizePerFile = 10 * 1024 * 1024, // 10MB default
  allowedTypes = [],
}) => {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) {
      return;
    }

    const newAttachments: AttachmentFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i]!; // Non-null assertion: files[i] exists when i < files.length

      // Check file count limit
      if (attachments.length + newAttachments.length >= maxFiles) {
        alert(`Maximum ${maxFiles} files allowed`);
        break;
      }

      // Check file size
      if (file.size > maxSizePerFile) {
        alert(
          `File "${file.name}" is too large. Maximum size is ${formatFileSize(maxSizePerFile)}`
        );
        continue;
      }

      // Check file type if restrictions exist
      if (allowedTypes.length > 0) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !allowedTypes.includes(`.${fileExtension}`)) {
          alert(`File type ".${fileExtension}" is not allowed`);
          continue;
        }
      }

      newAttachments.push({
        file,
        id: `${Date.now()}-${i}`,
        progress: 0,
      });
    }

    if (newAttachments.length > 0) {
      onAttachmentsChange([...attachments, ...newAttachments]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (id: string) => {
    onAttachmentsChange(attachments.filter(att => att.id !== id));
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label className={styles.label}>Attachments</label>
        <Button
          appearance="subtle"
          icon={<AttachRegular />}
          onClick={handleButtonClick}
          disabled={attachments.length >= maxFiles}
        >
          Add Files
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        accept={allowedTypes.length > 0 ? allowedTypes.join(',') : undefined}
      />

      {attachments.length > 0 && (
        <>
          <div className={styles.warning}>
            ⚠️ Attachments will be automatically deleted after 72 hours
          </div>

          <div className={styles.fileList}>
            {attachments.map(attachment => (
              <div key={attachment.id} className={styles.fileCard}>
                <DocumentRegular className={styles.fileIcon} />
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{attachment.file.name}</div>
                  <div className={styles.fileSize}>{formatFileSize(attachment.file.size)}</div>
                  {attachment.progress !== undefined && attachment.progress < 100 && (
                    <ProgressBar value={attachment.progress / 100} />
                  )}
                </div>
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  className={styles.removeButton}
                  onClick={() => handleRemoveFile(attachment.id)}
                />
              </div>
            ))}
          </div>

          <Text size={200} style={{ color: '#605e5c' }}>
            {attachments.length} of {maxFiles} files • Total:{' '}
            {formatFileSize(attachments.reduce((sum, att) => sum + att.file.size, 0))}
          </Text>
        </>
      )}
    </div>
  );
};

export default AttachmentUploader;
